import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	reportDuplicateUSignalAction,
	VerfahrenResource,
	verfahrenSingleSelector,
} from '@schir-int-client/verfahren-shared';
import { AppState } from 'apps/int-client/src/app/shared/store/app-state.model';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
	addChangeEntryAction,
	addEntryBatchAction,
	addVermerkEntryAction,
	createUmschreibungEntryAction,
	deleteEntryAction,
	loadChangeEntriesAction,
	loadChangeEntriesSuccessAction,
	updateChangeEntryAction,
} from '../../../register-change/src/lib/register-change.actions';
import { changeEntryListSelector } from '../../../register-change/src/lib/register-change.selectors';
import {
	createBetragsKorrekturAction,
	createLaufendeNummerKorrekturAction,
	createTeilloeschenRegisterblattEntryAction,
	roeteRegisterblattEntryAction,
	roeteRegisterblattEntryPartiellAction,
} from './register.actions';
import { RegisterBlattService } from './register.service';
import { of } from 'rxjs';
import { AppNotificationService } from '@schir-int-client/tech';
import { doNothingAction } from '@schir-int-client/ngrx-helpers';
import { ChangeEntryListLinkRel, ChangeEntryListResource } from '@schir-int-client/register-change';

@Injectable()
export class RegisterBlattEffects {
	private readonly actions = inject(Actions);
	private readonly store = inject(Store<AppState>);
	private readonly service = inject(RegisterBlattService);
	private readonly notificationService = inject(AppNotificationService);

	loadChangeEntries = createEffect(() =>
		this.actions.pipe(
			ofType(loadChangeEntriesAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([action, verfahren]) =>
				this.service
					.getOne(action.verfahren)
					.pipe(map(resultList => this.createLoadSuccessAction(verfahren, resultList)))
			)
		)
	);

	roetenEntry = createEffect(() =>
		this.actions.pipe(
			ofType(roeteRegisterblattEntryAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntry }, verfahren]) => {
				return this.service.createRoetenEntry(changeEntry).pipe(
					map(resultList => this.createLoadSuccessAction(verfahren, resultList)),
					catchError(err => this.catchDuplicateUsignalError(this.notificationService, err))
				);
			})
		)
	);

	deleteEntry = createEffect(() =>
		this.actions.pipe(
			ofType(deleteEntryAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntry }, verfahren]) =>
				this.service.deleteEntry(changeEntry).pipe(
					map(resultList => this.createLoadSuccessAction(verfahren, resultList)),
					catchError(err => this.catchDuplicateUsignalError(this.notificationService, err))
				)
			)
		)
	);

	addChangeEntry = createEffect(() =>
		this.actions.pipe(
			ofType(addChangeEntryAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector), this.store.select(changeEntryListSelector)),
			mergeMap(([{ changeEntry }, verfahren, entryList]) =>
				this.service.addChangeEntry(changeEntry, entryList.resource).pipe(
					map(resultList => this.createLoadSuccessAction(verfahren, resultList)),
					catchError(err => this.catchDuplicateUsignalError(this.notificationService, err))
				)
			)
		)
	);

	addEntryBatch = createEffect(() =>
		this.actions.pipe(
			ofType(addEntryBatchAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector), this.store.select(changeEntryListSelector)),
			mergeMap(([{ batchEntries }, verfahren, entryList]) =>
				this.service.addBatchEntry(batchEntries, entryList.resource).pipe(
					map(resultList => this.createLoadSuccessAction(verfahren, resultList)),
					catchError(err => this.catchDuplicateUsignalError(this.notificationService, err))
				)
			)
		)
	);

	createUmschreibung = createEffect(() =>
		this.actions.pipe(
			ofType(createUmschreibungEntryAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntryResource, newChangeEntry }, verfahren]) => {
				return this.service
					.createUmschreibungEntry(changeEntryResource, newChangeEntry)
					.pipe(map(resultList => this.createLoadSuccessAction(verfahren, resultList)));
			})
		)
	);

	addVermerkEntry = createEffect(() =>
		this.actions.pipe(
			// SCHIFF-5205 - neuer Vermerk deaktiviert
			ofType(addVermerkEntryAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector), this.store.select(changeEntryListSelector)),
			mergeMap(([{ changeEntryVermerk }, verfahren, entryList]) =>
				this.service
					.addVermerkEntry(changeEntryVermerk, entryList.resource)
					.pipe(map(resultList => this.createLoadSuccessAction(verfahren, resultList)))
			)
		)
	);

	updateChangeEntry = createEffect(() =>
		this.actions.pipe(
			ofType(updateChangeEntryAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntry }, verfahren]) =>
				this.service.updateChangeEntry(changeEntry).pipe(
					map(resultList => this.createLoadSuccessAction(verfahren, resultList)),
					catchError(err => this.catchDuplicateUsignalError(this.notificationService, err))
				)
			)
		)
	);

	partiellRoetenEntry = createEffect(() =>
		this.actions.pipe(
			ofType(roeteRegisterblattEntryPartiellAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntry, roetungsBereich }, verfahren]) =>
				this.service.createPartielleRoetungEntry(changeEntry, roetungsBereich).pipe(
					map(resultList => this.createLoadSuccessAction(verfahren, resultList)),
					catchError(err => this.catchDuplicateUsignalError(this.notificationService, err))
				)
			)
		)
	);

	teilloeschungEntry = createEffect(() =>
		this.actions.pipe(
			ofType(createTeilloeschenRegisterblattEntryAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntry, teilloeschenWert }, verfahren]) =>
				this.service
					.createTeilloeschung(changeEntry, teilloeschenWert)
					.pipe(map(resultList => this.createLoadSuccessAction(verfahren, resultList)))
			)
		)
	);

	createLaufendeNummerKorrektur = createEffect(() =>
		this.actions.pipe(
			ofType(createLaufendeNummerKorrekturAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntry, laufendeNummerKorrekturWert }, verfahren]) =>
				this.service
					.createLaufendeNummerKorrektur(changeEntry, laufendeNummerKorrekturWert)
					.pipe(map(resultList => this.createLoadSuccessAction(verfahren, resultList)))
			)
		)
	);

	createBetragsKorrektur = createEffect(() =>
		this.actions.pipe(
			ofType(createBetragsKorrekturAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			mergeMap(([{ changeEntry, betragsKorrekturWert }, verfahren]) =>
				this.service
					.createBetragsKorrektur(changeEntry, betragsKorrekturWert)
					.pipe(map(resultList => this.createLoadSuccessAction(verfahren, resultList)))
			)
		)
	);

	private createLoadSuccessAction(verfahren: VerfahrenResource, entryList: ChangeEntryListResource) {
		if (verfahren._links.self.href === entryList._links[ChangeEntryListLinkRel.VERFAHREN].href) {
			return loadChangeEntriesSuccessAction({ changeEntries: entryList });
		}
		return doNothingAction();
	}

	private catchDuplicateUsignalError(notificationService, error) {
		const issue = error['issues'][0];
		if (issue['messageCode'] == 'usignal.already.assigned' && !issue['message']) {
			notificationService.handleError(
				'Das Unterscheidungssignal dieses Verfahrens ist doppelt vergeben.\n' +
					'Eine Korrektur des Unterscheidungssignals ist notwendig, bevor weitere Änderungen am Verfahren erfolgen können.'
			);
		}
		return of(reportDuplicateUSignalAction({ payload: error }));
	}
}
